import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { humanise } from '../utils/format';

// Shown in the app shell when a page exists but this person cannot open it — instead of the old
// silent bounce to the dashboard, which left someone with a stale link wondering what happened.
//
//   <NoAccess feature="enable_reports" />   the org has that page turned off
//   <NoAccess reason="role" />              their role cannot open it
//
// It says why, and who can change it. Admins get a button to Settings › Features.

// feature_settings key → the page's name as the sidebar writes it.
const FEATURE_LABELS = {
  enable_templates: 'Templates',
  enable_groups: 'Group services',
  enable_quotes: 'Quotes',
  enable_mileage: 'Mileage',
  enable_payslips: 'Payslips',
  enable_emar: 'Medications',
  enable_forms: 'Forms',
  enable_incidents: 'Incidents',
  enable_tasks: 'Tasks',
  enable_teams: 'Teams',
  enable_timesheets: 'Timesheets',
  enable_invoices: 'Invoices',
  enable_general_chat: 'Messages',
  enable_team_chats: 'Messages',
  enable_direct_messaging: 'Messages',
  enable_reimbursements: 'Reimbursements',
  enable_reports: 'Reports',
  enable_fleet: 'Fleet',
  enable_activity_log: 'Activity log',
  enable_pay_contractors: 'Pay contractors',
  enable_financials: 'Financials',
  enable_market_intel: 'Market intel',
};

export function featureLabel(feature) {
  const keys = (Array.isArray(feature) ? feature : [feature]).filter(Boolean);
  for (const k of keys) if (FEATURE_LABELS[k]) return FEATURE_LABELS[k];
  return humanise(String(keys[0] || '').replace(/^enable_/, '')) || 'This page';
}

export default function NoAccess({ feature, reason, label }) {
  const { user } = useAuth() || {};
  const { pathname } = useLocation();
  const why = reason || (feature ? 'feature' : 'role');
  const org = user?.orgName || 'your organisation';
  const name = label || featureLabel(feature);
  // The dashboard is itself limited to admin and office roles; never offer a link that would land
  // on this same screen.
  const dashboardOk = ['admin', 'office_user', 'super_admin'].includes(user?.role) && pathname !== '/';

  let title;
  let body;
  let action = null;
  if (why === 'feature') {
    title = `${name} isn't turned on`;
    if (user?.role === 'admin') {
      body = `${name} is turned off for ${org}. You can turn it on in Settings › Features. If it isn't listed there, it isn't part of your plan yet — contact Rostery to add it.`;
      action = <Link to="/settings/features" className="btn-primary">Open Settings › Features</Link>;
    } else {
      body = `${name} is turned off for ${org}. An admin in your organisation can turn it on in Settings › Features.`;
    }
  } else {
    title = "You don't have access to this page";
    body = `Your role${user?.role ? ` (${humanise(user.role)})` : ''} can't open this page. If you need it, ask an admin in your organisation to change your access.`;
  }

  return (
    <div className="w-full max-w-2xl mx-auto py-4 sm:py-10">
      <div className="card p-6 sm:p-8">
        <span className="w-11 h-11 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center" aria-hidden="true">
          <Lock size={20} />
        </span>
        <h1 className="mt-5 text-lg font-semibold text-slate-900">{title}</h1>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{body}</p>
        <p className="mt-4 min-w-0">
          <span className="sr-only">Address you tried: </span>
          <code className="inline-block max-w-full truncate align-bottom rounded-md bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700" title={pathname}>
            {pathname}
          </code>
        </p>
        {(action || dashboardOk) && (
          <div className="mt-6 flex flex-wrap gap-2">
            {action}
            {dashboardOk && <Link to="/" className={action ? 'btn-secondary' : 'btn-primary'}>Go to dashboard</Link>}
          </div>
        )}
      </div>
    </div>
  );
}
